import React, { useState, useEffect } from "react";
import {
  Download,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { getDietPlanDashboard } from "../../api/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DietPlans = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dietData, setDietData] = useState(null);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [meals, setMeals] = useState([]);

  useEffect(() => {
    fetchDietPlan();
  }, []);

  const fetchDietPlan = async () => {
    try {
      setLoading(true);
      const response = await getDietPlanDashboard();

      if (response && response.status === true) {
        setDietData(response.data);
        setError(null);
        
        if (response.data?.current_day?.meals) {
          const formattedMeals = response.data.current_day.meals.map(meal => ({
            tag: meal.category || "Meal",
            title: meal.items?.[0]?.name || "Meal Item",
            desc: meal.items?.[0]?.description || "No description",
            img: meal.items?.[0]?.image_url || null,
            cal: meal.items?.[0]?.calories || 0,
            p: meal.items?.[0]?.protein || 0,
            c: meal.items?.[0]?.carbs || 0,
            f: meal.items?.[0]?.fat || 0,
            done: false,
            icon: meal.icon || "🍳"
          }));
          setMeals(formattedMeals);
        }
      } else {
        setError(response?.message || "Failed to fetch diet plan");
      }
    } catch (err) {
      console.error("Error fetching diet plan:", err);
      setError(err.message || "Failed to fetch diet plan");
    } finally {
      setLoading(false);
    }
  };

  const truncateTitle = (title, maxLength = 14) => {
    if (!title) return "Meal";
    if (title.length <= maxLength) return title;
    return title.slice(0, maxLength) + "...";
  };

  const handlePrevDay = () => {
    if (currentDayIndex > 0) {
      setCurrentDayIndex(currentDayIndex - 1);
      updateMealsForDay(currentDayIndex - 1);
    }
  };

  const handleNextDay = () => {
    if (dietData?.all_days && currentDayIndex < dietData.all_days.length - 1) {
      setCurrentDayIndex(currentDayIndex + 1);
      updateMealsForDay(currentDayIndex + 1);
    }
  };

  const updateMealsForDay = (index) => {
    const dayData = dietData?.all_days?.[index];
    if (dayData?.meals) {
      const formattedMeals = dayData.meals.map(meal => ({
        tag: meal.category || "Meal",
        title: meal.name || "Meal Item",
        desc: meal.description || "No description",
        img: meal.image_url || null,
        cal: meal.calories || 0,
        p: meal.protein || 0,
        c: meal.carbs || 0,
        f: meal.fat || 0,
        done: false,
        icon: meal.icon || "🍳"
      }));
      setMeals(formattedMeals);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Today";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getDayDisplay = () => {
    if (!dietData?.all_days) return "Day 1";
    const day = dietData.all_days[currentDayIndex];
    return day?.title || `Day ${currentDayIndex + 1}`;
  };

  const getWeeklyAdherence = () => {
    const adherence = dietData?.plan_info?.weekly_adherence;
    if (typeof adherence === 'object' && adherence !== null) {
      if (adherence.value !== undefined) return adherence.value;
      const values = Object.values(adherence);
      if (values.length > 0 && typeof values[0] === 'number') {
        return values[0];
      }
      return 85;
    }
    if (typeof adherence === 'number') return adherence;
    return 85;
  };

  // ✅ PDF Download Function
  const downloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    doc.setFontSize(22);
    doc.setTextColor(40, 40, 40);
    doc.text("Diet Plan", pageWidth / 2, yPos, { align: "center" });
    yPos += 10;

    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, yPos, { align: "center" });
    yPos += 15;

    doc.setDrawColor(200, 200, 200);
    doc.line(15, yPos, pageWidth - 15, yPos);
    yPos += 12;

    const planInfo = dietData?.plan_info || {};

    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.setFont("helvetica", "bold");
    doc.text("Plan Overview", 20, yPos);
    yPos += 8;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);

    const planDetails = [
      [`Plan Name:`, planInfo?.name || "Diet Plan"],
      [`Status:`, planInfo?.status || "Active"],
      [`Started:`, formatDate(planInfo?.started_date)],
      [`Progress:`, `${planInfo?.progress_percentage || 0}%`],
      [`Week:`, `${planInfo?.current_week || 0} of ${planInfo?.total_weeks || 1}`],
    ];

    planDetails.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, 20, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(value, 70, yPos);
      yPos += 7;
    });

    yPos += 5;

    const dailyTarget = dietData?.daily_target || {};
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40, 40, 40);
    doc.text("Daily Target", 20, yPos);
    yPos += 8;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);

    const targetDetails = [
      [`Calories:`, `${dailyTarget?.calories || 0} kcal`],
      [`Protein:`, `${dailyTarget?.protein || 0}g`],
      [`Carbs:`, `${dailyTarget?.carbs || 0}g`],
      [`Fats:`, `${dailyTarget?.fats || 0}g`],
    ];

    targetDetails.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, 20, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(value, 70, yPos);
      yPos += 7;
    });

    yPos += 5;

    doc.setDrawColor(200, 200, 200);
    doc.line(15, yPos, pageWidth - 15, yPos);
    yPos += 10;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40, 40, 40);
    doc.text(`Day ${currentDayIndex + 1} - ${getDayDisplay()} Meals`, 20, yPos);
    yPos += 8;

    const mealData = meals.map(meal => [
      meal.icon || "🍳",
      meal.tag || "Meal",
      truncateTitle(meal.title, 20),
      `${meal.cal}`,
      `${meal.p}g`,
      `${meal.c}g`,
      `${meal.f}g`,
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [["", "Meal", "Name", "Cal", "P", "C", "F"]],
      body: mealData,
      theme: "striped",
      headStyles: {
        fillColor: [87, 178, 106],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: "bold",
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 25 },
        2: { cellWidth: 50 },
        3: { cellWidth: 15, halign: "center" },
        4: { cellWidth: 15, halign: "center" },
        5: { cellWidth: 15, halign: "center" },
        6: { cellWidth: 15, halign: "center" },
      },
      margin: { left: 20, right: 20 },
    });

    const finalY = doc.lastAutoTable?.finalY || yPos + 50;
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text("Thank you for using Nutrillion!", pageWidth / 2, finalY + 10, { align: "center" });
    doc.text(`Plan: ${planInfo?.name || "Diet Plan"}`, pageWidth / 2, finalY + 17, { align: "center" });

    doc.save(`Diet_Plan_${planInfo?.name || "MyPlan"}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <div className="w-full font-lato">
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#57b26a]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full font-lato">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-500">{error}</p>
          <button 
            onClick={fetchDietPlan}
            className="mt-4 px-4 py-2 bg-[#57b26a] text-white rounded-lg hover:bg-[#49aa5d]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!dietData) {
    return (
      <div className="w-full font-lato">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <p className="text-yellow-600">No active diet plan</p>
        </div>
      </div>
    );
  }

  const weeklyAdherence = getWeeklyAdherence();

  return (
    <div className="w-full font-lato">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] leading-tight">
            Diet Plans
          </h2>
          <p className="text-[#98a19b] text-sm mt-2">
            View your active nutrition plan, log your meals, and track your
            adherence.
          </p>
        </div>

        <button 
          onClick={downloadPDF}
          className="h-[42px] px-5 rounded-md border border-[#dfe5df] text-[13px] text-[#4f5b56] hover:bg-gray-50 flex items-center gap-2 w-fit"
        >
          <Download size={14} />
          Download PDF
        </button>
      </div>

      {/* Plan Overview */}
      <div className="bg-white border border-[#e8ece8] rounded-xl p-6 mb-8">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-[11px] font-medium ${
                dietData?.plan_info?.status === "Active" 
                  ? "bg-[#58b96b] text-white" 
                  : "bg-gray-300 text-gray-600"
              }`}>
                {dietData?.plan_info?.status || "Active"}
              </span>
              <span className="text-[12px] text-[#98a19b]">
                Started: {formatDate(dietData?.plan_info?.started_date)}
              </span>
            </div>

            <h3 className="text-xl sm:text-xl font-semibold text-[#24312d] leading-tight mb-3">
              {dietData?.plan_info?.name || "Diet Plan"}
            </h3>

            <p className="text-sm text-[#98a19b] leading-6 max-w-[560px] mb-6">
              {dietData?.plan_info?.description || "A balanced plan designed for your health goals."}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              <div>
                <p className="text-sm text-[#98a19b] mb-1">Daily Target</p>
                <h4 className="text-xl sm:text-xl font-semibold text-[#24312d]">
                  {dietData?.daily_target?.calories || 0} kcal
                </h4>
              </div>

              <div>
                <p className="text-sm text-[#98a19b] mb-1">Protein</p>
                <h4 className="text-xl sm:text-xl font-semibold text-[#24312d]">
                  {dietData?.daily_target?.protein || 0}g
                </h4>
              </div>

              <div>
                <p className="text-sm text-[#98a19b] mb-1">Carbs</p>
                <h4 className="text-xl sm:text-xl font-semibold text-[#24312d]">
                  {dietData?.daily_target?.carbs || 0}g
                </h4>
              </div>

              <div>
                <p className="text-sm text-[#98a19b] mb-1">Fats</p>
                <h4 className="text-xl sm:text-xl font-semibold text-[#24312d]">
                  {dietData?.daily_target?.fats || 0}g
                </h4>
              </div>
            </div>
          </div>

          <div className="xl:border-l xl:border-[#edf1ed] xl:pl-8">
            <div className="mb-7">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-medium text-[#24312d]">
                  Plan Progress (Week {dietData?.plan_info?.current_week || 0} of {dietData?.plan_info?.total_weeks || 1})
                </span>
                <span className="text-[13px] font-semibold text-[#24312d]">
                  {dietData?.plan_info?.progress_percentage || 0}%
                </span>
              </div>

              <div className="h-[6px] bg-[#edf1ed] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#58b96b] rounded-full"
                  style={{ width: `${dietData?.plan_info?.progress_percentage || 0}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-medium text-[#24312d]">
                  Weekly Adherence
                </span>
                <span className="text-[13px] font-semibold text-[#58b96b]">
                  {weeklyAdherence}% (Excellent)
                </span>
              </div>

              <div className="h-[6px] bg-[#edf1ed] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#58b96b] rounded-full"
                  style={{ width: `${weeklyAdherence}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Meals */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
        <h3 className="text-[22px] font-semibold text-[#24312d]">
          {getDayDisplay()} - Meals
        </h3>

        <div className="flex items-center gap-2">
          <button 
            className="w-9 h-9 border border-[#dfe5df] rounded-md flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handlePrevDay}
            disabled={currentDayIndex === 0}
          >
            <ChevronLeft size={15} />
          </button>

          <button className="h-9 px-4 border border-[#dfe5df] rounded-md text-[13px] text-[#4f5b56]">
            {getDayDisplay()}
          </button>

          <button 
            className="w-9 h-9 border border-[#dfe5df] rounded-md flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleNextDay}
            disabled={!dietData?.all_days || currentDayIndex >= dietData.all_days.length - 1}
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {meals.length > 0 ? (
          meals.map((meal, index) => (
            <div
              key={index}
              className="bg-white border border-[#e8ece8] rounded-xl overflow-hidden"
            >
              {/* Image section - Only show if image exists */}
              {meal.img && (
                <div className="relative h-[190px]">
                  <img
                    src={meal.img}
                    alt={meal.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <span className="absolute top-3 left-3 bg-white text-[#24312d] text-[11px] px-3 py-1 rounded-full font-medium">
                    {meal.tag}
                  </span>
                </div>
              )}

              {/* If no image, show meal tag without image container */}
              {!meal.img && (
                <div className="pt-4 px-4">
                  <span className="inline-block bg-[#f0f4f0] text-[#24312d] text-[11px] px-3 py-1 rounded-full font-medium">
                    {meal.tag}
                  </span>
                </div>
              )}

              <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h4 className="text-[20px] font-semibold text-[#24312d] leading-snug">
                    {truncateTitle(meal.title)}
                  </h4>

                  {/* {meal.done ? (
                    <CheckCircle2
                      size={18}
                      className="text-[#58b96b] mt-1 shrink-0"
                    />
                  ) : (
                    <Circle
                      size={18}
                      className="text-[#98a19b] mt-1 shrink-0"
                    />
                  )} */}
                </div>

                <p className="text-[13px] text-[#98a19b] leading-6 mb-4">
                  {meal.desc}
                </p>

                <div className="flex flex-wrap gap-2 mb-5">
                  <span className="px-2 py-1 bg-[#f7f9f7] rounded text-[11px] text-[#4f5b56]">
                    Cal: {meal.cal}
                  </span>
                  <span className="px-2 py-1 bg-[#f7f9f7] rounded text-[11px] text-[#4f5b56]">
                    P: {meal.p}g
                  </span>
                  <span className="px-2 py-1 bg-[#f7f9f7] rounded text-[11px] text-[#4f5b56]">
                    C: {meal.c}g
                  </span>
                  <span className="px-2 py-1 bg-[#f7f9f7] rounded text-[11px] text-[#4f5b56]">
                    F: {meal.f}g
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-[#98a19b]">
            No meals found for this day.
          </div>
        )}
      </div>
    </div>
  );
};

export default DietPlans;